import { ChangeEvent, Dispatch, FC, useEffect, useState } from 'react'

import { AxiosError } from 'axios'
import {
  Button,
  Card,
  Grid,
  Input,
  Row,
  Select,
  SimpleCollapse
} from 'components'
import { useAuth, useLoading, useModal } from 'hooks'
import {
  ParamFaleConoscoResponseDto,
  TalkWithUsServices
} from 'services/NGT/Parameterization'
import TalkToUsService, { ITalkToUsEntityResponse } from 'services/NGT/TalkToUs'
import { COLORS, SPACING } from 'utils'

interface IAddNewTalkToUsProps {
  setSelectedTab: Dispatch<React.SetStateAction<number>>
}

const DEFAULT_TALK_TO_US: ITalkToUsEntityResponse = {
  dtEnvio: '',
  faleConoscoId: 0,
  idParamFaleConosco: 0,
  idUsuario: 0,
  txtMensagem: '',
  txtResposta: '',
  contrato: ''
}

const AddNewTalkToUs: FC<IAddNewTalkToUsProps> = ({ setSelectedTab }) => {
  const { showModal } = useModal()
  const { setLoading } = useLoading()
  const { user } = useAuth()

  const [talkToUs, setTalkToUs] =
    useState<ITalkToUsEntityResponse>(DEFAULT_TALK_TO_US)
  const [parametrizationTalkToUs, setParametrizationTalkToUs] = useState<
    ParamFaleConoscoResponseDto[]
  >([])

  const handleReturn = () => setSelectedTab(() => 0)

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTalkToUs((oldData) => {
      return {
        ...oldData,
        [event.target.name]: event.target.value
      }
    })
  }

  const getTalkToUsParametrization = () => {
    setLoading('loading')

    TalkWithUsServices()
      .getAllAutocomplete()
      .then(({ data }) => {
        if (data) {
          setParametrizationTalkToUs(data)
        }
      })
      .catch((error) => {
        const axiosError = error as AxiosError

        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message:
            axiosError.message ||
            'Não foi possivel obter as parametrizações de fale conosco',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
      .finally(() => setLoading('idle'))
  }

  useEffect(() => {
    getTalkToUsParametrization()

    setTalkToUs({
      ...DEFAULT_TALK_TO_US,
      dtEnvio: new Date(),
      idUsuario: user.IdUsuario
    })
  }, [])

  const handleSaveTalkToUs = () => {
    setLoading('loading')
    // Alterado para o incidente 694160-1 - Thales 02/06/2025
    // Crie o objeto JSON que você deseja salvar (não altera o valor visível para o usuário)
    const messageObject = {
      message: talkToUs.txtMensagem,  // Mantém a mensagem original digitada pelo usuário
      CDContrato: talkToUs.contrato,  // Inclui o contrato
    };

    // Agora, quando enviar para o backend, você passa o objeto JSON serializado
    const dataToSend = {
      ...talkToUs,  // Aqui preservamos todos os dados existentes de talkToUs
      txtMensagem: JSON.stringify(messageObject),  // Serializa o objeto JSON para o backend
    };

    TalkToUsService()
      .post(dataToSend)
      .then(() => {
        showModal({
          handleConfirm: handleReturn,
          hideButtonCancel: true,
          message:
            'Mensagem enviada com sucesso. Sua solicitação será atendida pela área responsável.',
          txtButtonConfirm: 'OK',
          type: 'success'
        })
      })
      .catch((error) => {
        const axiosError = error as AxiosError
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: axiosError.message || 'Não foi enviar a mensagem!',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
      .finally(() => setLoading('idle'))
  }

  const validateTalkToUsMessage = () => {
    return (
      talkToUs.txtMensagem.length &&
      talkToUs.contrato.length &&
      talkToUs.idUsuario &&
      talkToUs.idParamFaleConosco
    )
  }

  return (
    <Card
      marginLeft={SPACING.spacing20}
      marginRight={SPACING.spacing20}
      padding={SPACING.spacing20}
      boxShadow={`0 0 1em ${COLORS.lightGray}`}
    >
      <SimpleCollapse title="Fale Conosco" textColor={COLORS.black}>
        <Grid
          gridCols="1fr 1.5fr"
          gridGap={`0 ${SPACING.spacing20}`}
          margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
        >
          <Select
            label="Motivo"
            formValue="idParamFaleConosco"
            value={talkToUs.idParamFaleConosco}
            onChange={handleOnChange}
            clearSelect
            defaultPlaceholderValue={0}
            key="idParamFaleConosco"
            labelMenu="dsMotivo"
            valueMenu="idParamFaleConosco"
            listOptions={parametrizationTalkToUs}
          />
          <Input
            label="Contrato"
            value={talkToUs.contrato}
            onChange={handleOnChange}
            multiline
            maxLength={4000}
            formValue="contrato"
            placeholder="Contrato"
          />
        </Grid>
        <Grid
          gridCols="1fr"
          gridGap={`0 ${SPACING.spacing20}`}
          margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
        >
          <Input
            label="Mensagem"
            value={talkToUs.txtMensagem}
            onChange={handleOnChange}
            multiline
            maxLength={4000}
            formValue="txtMensagem"
            placeholder="Digite aqui a sua mensagem"
          />
        </Grid>
        <Row justifyContent="flex-end" marginTop={SPACING.spacing10}>
          <Button
            text="Salvar"
            disabled={!validateTalkToUsMessage()}
            onClick={handleSaveTalkToUs}
            marginLeft={SPACING.spacing15}
            marginRight={SPACING.spacing15}
          />
        </Row>
      </SimpleCollapse>
    </Card>
  )
}

export default AddNewTalkToUs
